import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './FuturesAnalysis.css';

function FuturesAnalysis({ settings }) {
  const [transactions, setTransactions] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [currentPrice, setCurrentPrice] = useState(0);
  const [lastUpdate, setLastUpdate] = useState('');
  const [lastTransactionPrice, setLastTransactionPrice] = useState(0);
  const [viewFilter, setViewFilter] = useState('همه');
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    if (selectedSymbol) {
      fetchMarketPrice(selectedSymbol);
      getLastTransactionPriceForSymbol(selectedSymbol);
      const interval = setInterval(() => fetchMarketPrice(selectedSymbol), 30000);
      return () => clearInterval(interval);
    }
  }, [selectedSymbol]);

  const fetchTransactions = async () => {
    try {
      const data = await api.get('/api/transactions');
      const futureData = data.filter(t => t.MarketType === 'Future');
      setTransactions(futureData);
      const uniqueSymbols = [...new Set(futureData.map(t => t.Symbol))];
      setSymbols(uniqueSymbols);
      if (uniqueSymbols.length > 0) {
        setSelectedSymbol(uniqueSymbols[0]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const fetchMarketPrice = async (symbol) => {
    try {
      const data = await api.get(`/api/market_prices/${encodeURIComponent(symbol)}`);
      if (data && data.price > 0) {
        setCurrentPrice(data.price);
        setLastUpdate(data.last_update || '');
      } else {
        const lastPrice = getLastTransactionPriceForSymbol(symbol);
        setCurrentPrice(lastPrice);
        setLastUpdate('آخرین معامله');
      }
    } catch (error) {
      console.error('Error fetching price:', error);
      const lastPrice = getLastTransactionPriceForSymbol(symbol);
      setCurrentPrice(lastPrice);
      setLastUpdate('آخرین معامله');
    }
  };

  const getLastTransactionPriceForSymbol = (symbol) => {
    const symbolTxs = transactions.filter(t => t.Symbol === symbol);
    if (symbolTxs.length > 0) {
      const lastPrice = symbolTxs[symbolTxs.length - 1].Price;
      setLastTransactionPrice(lastPrice);
      return lastPrice;
    }
    return 0;
  };

  const getCommissionRate = (marketType, transactionType) => {
    if (transactionType === 'bonus') return 0;
    const settingsMap = {};
    settings.forEach(s => { settingsMap[s.key] = s.value; });
    const key = `${marketType}_${transactionType}_comm`.toLowerCase();
    return settingsMap[key] || 0;
  };

  const getBankInterestRate = () => {
    const settingsMap = {};
    settings.forEach(s => { settingsMap[s.key] = s.value; });
    return settingsMap['bank_interest_rate'] || 0.20;
  };

  const calculateFuturesLogic = (symbol, currentPrice, filter) => {
    const symbolTx = transactions.filter(t => t.Symbol === symbol);
    if (symbolTx.length === 0) return { rows: [], totalInvested: 0, totalMarketPnl: 0, totalOppCost: 0 };
    
    const bankRate = getBankInterestRate();
    const uniqueSteps = [...new Set(symbolTx.map(t => t.Step))];
    
    const displayRows = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let grandInvested = 0;
    let grandPnl = 0;
    let grandOpp = 0;
    
    for (const step of uniqueSteps) {
      const stepTx = symbolTx.filter(t => t.Step === step).sort((a, b) => new Date(a.Date) - new Date(b.Date));
      if (stepTx.length === 0) continue;
      
      const firstDate = new Date(stepTx[0].Date);
      const lastDate = new Date(stepTx[stepTx.length - 1].Date);
      
      let netQty = 0;
      let marginInvested = 0;
      let totalLongCost = 0;
      let totalShortRevenue = 0;
      let accumulatedOppCost = 0;
      let lastCalcDate = null;
      
      for (const tx of stepTx) {
        const currDate = new Date(tx.Date);
        const rtype = tx.Type;
        const qty = parseFloat(tx.Quantity);
        const price = parseFloat(tx.Price);
        const blocked = parseFloat(tx.BlockedAmount) || 0;
        const comm = getCommissionRate('Future', rtype);
        
        if (lastCalcDate !== null) {
          const diffTime = Math.abs(currDate - lastCalcDate);
          const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          if (days > 0 && marginInvested > 0 && netQty !== 0) {
            accumulatedOppCost += marginInvested * bankRate * (days / 365);
          }
        }
        lastCalcDate = currDate;
        
        if (rtype === 'buy') {
          netQty += qty;
          const costVal = (qty * price) * (1 + comm);
          totalLongCost += costVal;
          marginInvested += blocked;
        } else if (rtype === 'sell') {
          netQty -= qty;
          const revVal = (qty * price) * (1 - comm);
          totalShortRevenue += revVal;
          marginInvested += blocked;
        } else if (rtype === 'margin') {
          marginInvested += blocked;
        }
      }
      
      let status = '';
      let daysHeld = 0;
      
      if (Math.abs(netQty) < 0.001) {
        status = '🔴 بسته شده';
        const diffLast = Math.abs(lastDate - firstDate);
        daysHeld = Math.ceil(diffLast / (1000 * 60 * 60 * 24));
      } else if (netQty > 0) {
        status = '🟢 باز (Long 📈)';
        const diffFirst = Math.abs(today - firstDate);
        daysHeld = Math.ceil(diffFirst / (1000 * 60 * 60 * 24));
      } else {
        status = '🟢 باز (Short 📉)';
        const diffFirst = Math.abs(today - firstDate);
        daysHeld = Math.ceil(diffFirst / (1000 * 60 * 60 * 24));
      }
      
      if (Math.abs(netQty) > 0.001) {
        const diffTime = Math.abs(today - lastCalcDate);
        const daysToNow = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (daysToNow > 0) {
          accumulatedOppCost += marginInvested * bankRate * (daysToNow / 365);
        }
      }
      
      const realizedPnl = totalShortRevenue - totalLongCost;
      
      let unrealizedPnl = 0;
      if (Math.abs(netQty) > 0.001) {
        const commCurr = getCommissionRate('Future', netQty > 0 ? 'sell' : 'buy');
        if (netQty > 0) {
          const closingValue = (netQty * currentPrice) * (1 - commCurr);
          unrealizedPnl = closingValue;
        } else {
          const costToClose = Math.abs(netQty) * currentPrice * (1 + commCurr);
          unrealizedPnl = -costToClose;
        }
      }
      
      const marketPnl = realizedPnl + unrealizedPnl;
      const netPnl = marketPnl - accumulatedOppCost;
      
      let show = false;
      if (filter === 'همه') show = true;
      else if (filter === '🟢 باز' && netQty !== 0) show = true;
      else if (filter === '🔴 بسته شده' && Math.abs(netQty) < 0.001) show = true;
      
      if (show) {
        displayRows.push({
          step: step,
          status: status,
          daysHeld: daysHeld,
          netQty: parseInt(netQty),
          marginInvested: marginInvested,
          marketPnl: marketPnl,
          oppCost: accumulatedOppCost,
          netPnl: netPnl,
          rawPnl: netPnl,
          rawInvested: marginInvested
        });
        
        grandInvested += marginInvested;
        grandPnl += marketPnl;
        grandOpp += accumulatedOppCost;
      }
    }
    
    return { rows: displayRows, totalInvested: grandInvested, totalMarketPnl: grandPnl, totalOppCost: grandOpp };
  };

  const handleCalculate = () => {
    setLoading(true);
    setTimeout(() => {
      const { rows, totalInvested, totalMarketPnl, totalOppCost } = calculateFuturesLogic(selectedSymbol, currentPrice, viewFilter);
      
      const grandNet = totalMarketPnl - totalOppCost;
      
      const processedRows = rows.map(row => {
        let percent = 0;
        if (row.rawInvested !== 0) {
          percent = (row.rawPnl / row.rawInvested) * 100;
        }
        return {
          ...row,
          marginInvestedFormatted: row.marginInvested.toLocaleString(),
          marketPnlFormatted: row.marketPnl.toLocaleString(),
          oppCostFormatted: row.oppCost.toLocaleString(),
          netPnlFormatted: row.netPnl.toLocaleString(),
          percentFormatted: percent.toFixed(2) + '%',
          percentValue: percent
        };
      });
      
      setResultData({
        rows: processedRows,
        currentPriceWarning: currentPrice === 0
      });
      
      setSummary({
        totalInvested: totalInvested,
        totalMarketPnl: totalMarketPnl,
        totalOppCost: totalOppCost,
        grandNet: grandNet,
        selectedSymbol: selectedSymbol
      });
      
      setLoading(false);
    }, 100);
  };

  if (transactions.length === 0) {
    return (
      <div className="futures-analysis">
        <h1>📊 تحلیل پورتفوی آتی (Futures)</h1>
        <div className="info-box">داده آتی موجود نیست.</div>
      </div>
    );
  }

  return (
    <div className="futures-analysis">
      <h1>📊 تحلیل پورتفوی آتی (Futures)</h1>
      
      <div className="filters-section">
        <div className="filter-group">
          <label>انتخاب نماد آتی:</label>
          <select value={selectedSymbol} onChange={(e) => setSelectedSymbol(e.target.value)}>
            {symbols.map(sym => <option key={sym} value={sym}>{sym}</option>)}
          </select>
        </div>
        
        <div className="filter-group">
          <label>قیمت تسویه روزانه (آخرین: {lastTransactionPrice.toLocaleString()}):</label>
          <input 
            type="number" 
            value={currentPrice} 
            onChange={(e) => setCurrentPrice(parseFloat(e.target.value))}
            min={0}
            step={100}
          />
        </div>
        
        <div className="filter-group radio-group">
          <label>فیلتر وضعیت پوزیشن:</label>
          <div className="radio-options">
            <label className="radio-label">
              <input type="radio" value="همه" checked={viewFilter === 'همه'} onChange={(e) => setViewFilter(e.target.value)} />
              همه
            </label>
            <label className="radio-label">
              <input type="radio" value="🟢 باز" checked={viewFilter === '🟢 باز'} onChange={(e) => setViewFilter(e.target.value)} />
              🟢 باز
            </label>
            <label className="radio-label">
              <input type="radio" value="🔴 بسته شده" checked={viewFilter === '🔴 بسته شده'} onChange={(e) => setViewFilter(e.target.value)} />
              🔴 بسته شده
            </label>
          </div>
        </div>
        
        <button className="calculate-btn" onClick={handleCalculate} disabled={loading}>
          {loading ? 'در حال محاسبه...' : 'محاسبه آتی'}
        </button>
      </div>
      
      {resultData && resultData.rows.length > 0 && (
        <div className="results-section">
          {resultData.currentPriceWarning && (
            <div className="warning-box">
              ⚠️ توجه: قیمت تسویه روزانه 0 وارد شده است. سود/زیان ممکن است اشتباه باشد.
            </div>
          )}
          
          <div className="table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>پله</th>
                  <th>وضعیت</th>
                  <th>تعداد روز</th>
                  <th>تعداد خالص</th>
                  <th>وجه تضمین (سرمایه)</th>
                  <th>سود/زیان بازار</th>
                  <th>هزینه فرصت</th>
                  <th>عملکرد خالص</th>
                  <th>درصد عملکرد</th>
                </tr>
              </thead>
              <tbody>
                {resultData.rows.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.step}</td>
                    <td className={row.status.includes('Long') ? 'status-long' : row.status.includes('Short') ? 'status-short' : 'status-closed'}>
                      {row.status}
                    </td>
                    <td>{row.daysHeld}</td>
                    <td>{row.netQty}</td>
                    <td>{row.marginInvestedFormatted}</td>
                    <td className={row.marketPnl < 0 ? 'negative' : 'positive'}>{row.marketPnlFormatted}</td>
                    <td className="opp-cost">{row.oppCostFormatted}</td>
                    <td className={row.netPnl < 0 ? 'negative' : 'positive'}>{row.netPnlFormatted}</td>
                    <td className={row.percentValue < 0 ? 'negative' : 'positive'}>{row.percentFormatted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="summary-cards">
            <div className="card">
              <div className="card-label">کل وجه تضمین</div>
              <div className="card-value">{summary.totalInvested.toLocaleString()} ریال</div>
            </div>
            <div className="card">
              <div className="card-label">سود بازار</div>
              <div className="card-value">{summary.totalMarketPnl.toLocaleString()} ریال</div>
            </div>
            <div className="card">
              <div className="card-label">هزینه فرصت</div>
              <div className="card-value">{summary.totalOppCost.toLocaleString()} ریال</div>
            </div>
            <div className="card">
              <div className="card-label">عملکرد خالص</div>
              <div className={`card-value ${summary.grandNet < 0 ? 'negative' : 'positive'}`}>
                {summary.grandNet.toLocaleString()} ریال
                {summary.totalInvested > 0 && (
                  <span className="delta">({((summary.grandNet / summary.totalInvested) * 100).toFixed(2)}%)</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {resultData && resultData.rows.length === 0 && (
        <div className="warning-box">داده‌ای با فیلتر انتخاب شده یافت نشد.</div>
      )}
    </div>
  );
}

export default FuturesAnalysis;
